import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import {
  MessageCircle, Building2, Rocket, ExternalLink, Send, AlertCircle, Inbox, Linkedin
} from "lucide-react";
import Avatar from "./Avatar";
import { Loader, trackPointer } from "./UI";
import { DEFAULT_BIO, getValidUrl } from "../utils/Utils";

/** Columna de fichas (empresas o proyectos) con su estado vacío. */
function Section({ icon: Icon, title, items, empty, render }) {
  return (
    <section>
      <div className="dyd-section-title">
        <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 9 }}>
          <Icon size={18} style={{ color: "var(--dyd-blue-light)" }} />
          {title}
        </h3>
      </div>

      {items.length > 0 ? (
        <div className="dyd-item-grid">{items.map(render)}</div>
      ) : (
        <div className="dyd-empty" style={{ padding: "38px 20px" }}>
          <Inbox size={30} strokeWidth={1.3} />
          <h3 style={{ fontSize: "0.95rem" }}>{empty}</h3>
        </div>
      )}
    </section>
  );
}

export default function ProfileUserComponent({ profile, companies, projects, enviarMensaje, loading }) {
  const [showModal, setShowModal] = useState(false);
  const [messageData, setMessageData] = useState({ message: "", email: "" });

  const handleCloseModal = () => {
    setShowModal(false);
    setMessageData({ message: "", email: "" });
  };

  const handleShowModal = () => setShowModal(true);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMessageData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitMessage = (e) => {
    e.preventDefault();
    enviarMensaje(messageData.message, messageData.email);
    handleCloseModal();
  };

  if (loading) return <Loader text="Cargando perfil" />;

  if (!profile) {
    return (
      <div className="dyd-page" style={{ display: "grid", placeItems: "center" }}>
        <div className="dyd-empty" style={{ maxWidth: 460 }}>
          <AlertCircle size={42} strokeWidth={1.3} />
          <h3>Error al cargar el perfil</h3>
          <p>Por favor, inténtalo de nuevo.</p>
        </div>
      </div>
    );
  }

  const acceptedCompanies = companies || [];
  const acceptedProjects = projects?.filter((p) => p.accepted !== false) || [];

  return (
    <div className="dyd-page">
      <div className="dyd-profile-inner">

        {/* ─── Cabecera ─── */}
        <header className="dyd-profile-hero dyd-rise">
          <div
            className={`dyd-profile-cover ${profile.coverPicture ? "has-image" : ""}`}
            style={profile.coverPicture ? { backgroundImage: `url(${profile.coverPicture})` } : undefined}
          />

          <div className="dyd-profile-identity">
            <Avatar
              src={profile.profilePicture}
              name={profile.name}
              surname={profile.surname}
              size={128}
              ring
            />

            <div className="dyd-profile-name">
              <h1>{profile.name} {profile.surname}</h1>
              <span className="dyd-chip">Miembro del club</span>
            </div>

            <button className="dyd-btn dyd-btn-primary" onClick={handleShowModal}>
              <MessageCircle size={16} />
              Contactar
            </button>
          </div>
        </header>

        {/* ─── Bio ─── */}
        <div className="dyd-panel dyd-profile-bio dyd-rise">
          <p className="dyd-lead" style={{ margin: 0 }}>{profile.bio || DEFAULT_BIO}</p>

          {profile.networkingCategories?.length > 0 && (
            <div className="dyd-cat-chips" style={{ marginTop: 14 }}>
              {profile.networkingCategories.map((category) => (
                <span key={category} className="dyd-chip">{category}</span>
              ))}
            </div>
          )}

          {profile.linkedin && (
            <a
              className="dyd-btn dyd-btn-ghost dyd-btn-sm"
              href={getValidUrl(profile.linkedin)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ alignSelf: "flex-start", marginTop: 14 }}
            >
              <Linkedin size={15} /> Ver LinkedIn
              <ExternalLink size={13} />
            </a>
          )}
        </div>

        {/* ─── Fichas ─── */}
        <div className="dyd-pro-grid dyd-rise" style={{ "--i": 1 }}>
          <Section
            icon={Building2}
            title="Ficha profesional"
            items={acceptedCompanies}
            empty="Sin empresas añadidas"
            render={(company, idx) => (
              <div key={company.name + idx} className="dyd-item-card" onMouseMove={trackPointer}>
                <span className="dyd-item-title">
                  {company.name}
                </span>
                <span className="dyd-item-text">{company.description}</span>
                {company.role && <span className="dyd-chip">{company.role}</span>}
                {company.website && (
                  <a
                    href={getValidUrl(company.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dyd-item-link"
                  >
                    <ExternalLink size={12} /> {company.website}
                  </a>
                )}
              </div>
            )}
          />

          <Section
            icon={Rocket}
            title="Proyecto personal"
            items={acceptedProjects}
            empty="Sin proyectos añadidos"
            render={(project, idx) => (
              <div
                key={project.name + idx}
                className="dyd-item-card is-clickable"
                onMouseMove={trackPointer}
                onClick={() => (window.location.href = `/project/${project._id}`)}
              >
                <span className="dyd-item-title">{project.name}</span>
                <span className="dyd-item-text">{project.description}</span>
                {project.link && (
                  <a
                    href={getValidUrl(project.link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dyd-item-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={12} /> {project.link}
                  </a>
                )}
              </div>
            )}
          />
        </div>

        {/* ─── Modal de contacto ─── */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Enviar mensaje a {profile.name}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmitMessage}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Tu email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={messageData.email}
                  onChange={handleInputChange}
                  placeholder="Para que te pueda responder"
                  required
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Mensaje</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="message"
                  value={messageData.message}
                  onChange={handleInputChange}
                  placeholder={`Escribe tu mensaje para ${profile.name}…`}
                  required
                />
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>Cancelar</Button>
              <button className="dyd-btn dyd-btn-primary" type="submit">
                <Send size={15} /> Enviar mensaje
              </button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>

    </div>
  );
}
